/**
 * One group on the results page: the stream's first screen, then "load more" from `GET /search`.
 *
 * The stream carries each group's top hits and no cursor, so the first page asked for is the
 * group from its start — it repeats the rows already drawn, and they are dropped by key. After
 * that the keyset cursor walks on (§5.6). Rows are only ever added: exact matches the pages bring
 * go after the exact matches on screen, related ones after the related ones, and nothing drawn
 * moves up or disappears.
 */

import { useState } from 'react';

import { Button, InlineError } from '@/components';
import { useMessages } from '@/lib/i18n';

import type { SearchHit } from './api';
import { hitKey, PER_GROUP } from './model';
import type { GroupView } from './model';
import { searchErrorKey } from './problems';
import { useSearchGroup } from './queries';
import { SearchGroup } from './SearchGroup';
import { SearchHitRow } from './SearchHit';
import styles from './Search.module.css';

export interface SearchResultsGroupProps {
  /** The settled query the view belongs to; the cursor is bound to it. */
  readonly q: string;
  readonly view: GroupView;
  /** Only this group is on screen (a type tab) — "load more" instead of "show all". */
  readonly single: boolean;
  readonly onShowAll: () => void;
  readonly onOpen: (hit: SearchHit) => void;
}

export function SearchResultsGroup({ q, view, single, onShowAll, onOpen }: SearchResultsGroupProps) {
  const t = useMessages('search');
  // Nothing is asked of `GET /search` until somebody wants more of this one group.
  const [more, setMore] = useState(false);
  const pages = useSearchGroup(q, view.group, more);

  const streamed = [...view.lexical, ...view.semantic];
  const seen = new Set(streamed.map(hitKey));
  const extra: SearchHit[] = [];
  for (const hit of pages.data?.pages.flatMap((page) => page.items) ?? []) {
    const key = hitKey(hit);
    if (seen.has(key)) continue;
    seen.add(key);
    extra.push(hit);
  }
  const all = [...streamed, ...extra];
  const lexical = all.filter((hit) => hit.match === 'lexical');
  const semantic = all.filter((hit) => hit.match === 'semantic');

  // A group whose lexical set came back short has nothing more; one that came back full may (no
  // counts, §5a). Semantic hits do not count: they fill a short group without implying a page 2.
  const fullLexical = view.lexical.length >= PER_GROUP;
  const mayHaveMore = more ? pages.hasNextPage : fullLexical;

  return (
    <div className={styles.pageGroup}>
      <SearchGroup
        group={view.group}
        lexical={lexical}
        semantic={semantic}
        pending={view.pending}
        action={
          !single && fullLexical ? (
            <button type="button" className={styles.groupLink} onClick={onShowAll}>
              {t('group.showAll')}
            </button>
          ) : undefined
        }
        renderHit={(hit) => <SearchHitRow hit={hit} onOpen={onOpen} />}
      />
      {pages.isError ? <InlineError>{t(searchErrorKey(pages.error))}</InlineError> : null}
      {single && mayHaveMore ? (
        <div className={styles.more}>
          <Button
            variant="secondary"
            size="sm"
            loading={pages.isFetching}
            onClick={() => {
              if (!more) {
                setMore(true);
                return;
              }
              void pages.fetchNextPage();
            }}
          >
            {t('page.more')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
